<script lang="ts">
	/**
	 * One metric over time (§12.10), with the previous window drawn behind it.
	 *
	 * The metric is a control rather than eight charts, because eight charts of eight metrics is a
	 * page nobody scrolls to the bottom of — and the question is almost always "this one, over
	 * time", asked one metric at a time.
	 *
	 * The table under the chart is not a fallback. It is the relief the palette's light-mode
	 * contrast obliges, and it is also what somebody copies into a spreadsheet.
	 */

	import { Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel, SelectField, TrendChart } from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import { analyticsRetentionNote } from '../../../Utilities';
	import type { AnalyticsMetric, AnalyticsQuery, AnalyticsTimeseries } from '../../../AppTypes';
	import { getAnalyticsTimeseries } from '../../../AppNetwork';

	export let filter: AnalyticsQuery;

	const METRICS: { value: AnalyticsMetric; name: string; description?: string }[] = [
		{ value: 'sessions', name: 'Visits', description: 'Separate visits to the storefront; a visit ends after a stretch of inactivity.' },
		{ value: 'visitors', name: 'Visitors', description: 'Distinct browsers, however many times each came back.' },
		{ value: 'pageViews', name: 'Page views', description: 'Every storefront page shown.' },
		{ value: 'productViews', name: 'Product views', description: 'Product pages shown.' },
		{ value: 'engaged', name: 'Engaged the flow', description: 'Visits that started choosing a plan or options on a product page.' },
		{ value: 'checkouts', name: 'Reached payment', description: 'Visits that got as far as the payment step.' },
		{ value: 'purchases', name: 'Purchases', description: 'Visits that ended in a completed purchase.' },
		{ value: 'revenue', name: 'Revenue', description: 'What those purchases took, per currency.' }
	];

	let metric: AnalyticsMetric = 'sessions';
	let data: AnalyticsTimeseries | null = null;
	let error = '';
	let loading = false;

	/**
	 * Re-read whenever the filter or the metric moves.
	 *
	 * Keyed on a **string**, not on the objects: a reactive statement reading `filter` directly
	 * re-runs on this panel's own writes and fetches forever, which is the same trap the product
	 * page's `quoteKey` exists to avoid.
	 */
	$: queryKey = JSON.stringify({ filter, metric });
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		loading = true;
		try {
			data = await getAnalyticsTimeseries(filter, metric);
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the trend';
			data = null;
		} finally {
			loading = false;
		}
	}

	$: isMoney = metric === 'revenue';
	/*
	 * Whether the archive can carry the part of the line past the cutoff. Page and product views are
	 * event counts and additive; purchases and revenue never leave the money tables at all. Visits
	 * and visitors cannot be recovered, and the note says so rather than the chart drawing zeros.
	 */
	$: retentionNote = analyticsRetentionNote(
		data?.retention,
		// The metric the **response** describes, not the one the control currently holds: the two
		// differ for one render after somebody changes it, and a note that flickers between "drawn
		// from the archive" and "missing" while the chart is still the old one is worse than none.
		(data?.retention?.archivedMetrics ?? []).includes(data?.metric ?? '')
	);
	$: title = METRICS.find(entry => entry.value === metric)?.name ?? 'Visits';

	function fnBucket(bucket: string): string {
		const date = new Date(bucket);
		return Number.isNaN(date.getTime()) ? bucket : date.toISOString().slice(0, 10);
	}

	function fnValue(value: number, currency: string | null): string {
		if (!isMoney || currency === null) return value.toLocaleString();
		return $formatAmount(value, currency) ?? `${value} minor units`;
	}
</script>

<Panel title="Trend" faIcon="chart-area">
	<svelte:fragment slot="titleRight">
		<!-- Labelled for the reason every other control on this page is: a header dropdown showing
		     "Visits" says nothing about what else it could show. -->
		<label class="text-xs text-default shrink-0" for="trend-metric">Metric</label>
		<SelectField id="trend-metric" size="sm" class="w-48" bind:value={metric} items={METRICS} />
	</svelte:fragment>

	{#if error.length > 0}
		<EmptyState variant="error" title="The trend could not be loaded." description={error} />
	{:else if data === null}
		<EmptyState variant="loading" />
	{:else if data.series.length === 0}
		<EmptyState variant="empty" title="Nothing recorded in this range." />
	{:else}
		{#if retentionNote.length > 0}
			<p class="text-default text-xs mb-2">{retentionNote}</p>
		{/if}

		<div class:opacity-50={loading}>
			<TrendChart
				series={data.series}
				previousSeries={data.previousSeries}
				{title}
				{isMoney}
				granularity={data.granularity}
			/>
		</div>

		<div class="overflow-x-auto mt-3">
			<Table class="dataTable">
				<TableHead>
					<TableHeadCell>Bucket</TableHeadCell>
					{#each data.series as entry (entry.currency ?? 'count')}
						<TableHeadCell class="text-right tabular-nums">
							{entry.currency === null ? title : `${title} (${entry.currency.toUpperCase()})`}
						</TableHeadCell>
					{/each}
				</TableHead>
				<TableBody>
					{#each data.series[0].points as point, index (point.bucket)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">{fnBucket(point.bucket)}</TableBodyCell>
							{#each data.series as entry (entry.currency ?? 'count')}
								<TableBodyCell class="dataTable text-right tabular-nums">
									{fnValue(entry.points[index]?.value ?? 0, entry.currency)}
								</TableBodyCell>
							{/each}
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</div>
	{/if}
</Panel>
